import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Plus,
  ArrowRight,
  Trash2,
  Upload,
  CheckSquare,
} from 'lucide-react';
import type { Task } from '@/types/task';
import { demoAssignees } from '@/data/demo-tasks';

interface TaskDetailChecklistProps {
  task: Task;
  onTaskUpdate: (task: Task) => void;
  isEditMode?: boolean;
}

export function TaskDetailChecklist({ task, onTaskUpdate, isEditMode = false }: Readonly<TaskDetailChecklistProps>) {
  const [newChecklistItem, setNewChecklistItem] = useState('');

  const handleChecklistToggle = (checklistId: string) => {
    const updatedChecklists = task.checklists.map(item => {
      if (item.id === checklistId) {
        return {
          ...item,
          completed: !item.completed,
          completedAt: !item.completed ? new Date().toISOString() : undefined,
          completedBy: !item.completed ? 'Current User' : undefined,
        };
      }
      return item;
    });
    onTaskUpdate({ ...task, checklists: updatedChecklists });
  };

  const handleAddChecklistItem = () => {
    if (newChecklistItem.trim()) {
      const newItem = {
        id: `check-${Date.now()}`,
        title: newChecklistItem.trim(),
        completed: false,
        createdAt: new Date().toISOString(),
      };
      onTaskUpdate({
        ...task,
        checklists: [...task.checklists, newItem],
      });
      setNewChecklistItem('');
    }
  };

  const handleDeleteChecklistItem = (checklistId: string) => {
    onTaskUpdate({
      ...task,
      checklists: task.checklists.filter(item => item.id !== checklistId),
    });
  };

  const handleConvertToSubtask = (checklistItem: typeof task.checklists[0]) => {
    // R4.1: Checklist Item → Subtask Conversion
    const assigneeId = prompt(
      `Convert checklist item to subtask:\n"${checklistItem.title}"\n\nEnter assignee (leave blank to use current task assignee):`
    );
    
    if (assigneeId === null) return; // User cancelled
    
    // Remove from checklist and mark as converted
    const updatedChecklists = task.checklists.map(item => {
      if (item.id === checklistItem.id) {
        return {
          ...item,
          completed: true,
          completedAt: new Date().toISOString(),
          completedBy: 'System (Converted to Subtask)',
        };
      }
      return item;
    });
    
    // Add as subtask
    const finalAssigneeId = (assigneeId.trim() || task.assignee?.id) ?? '';
    const assignee = demoAssignees.find(a => a.id === finalAssigneeId);
    
    const newSubtask = {
      id: `sub-${Date.now()}`,
      title: checklistItem.title,
      status: 'New' as const,
      assigneeId: finalAssigneeId,
      assigneeName: assignee?.name ?? 'Unassigned',
    };
    
    onTaskUpdate({
      ...task,
      checklists: updatedChecklists,
      subtasks: [...(task.subtasks ?? []), newSubtask],
    });
  };

  const completedChecklists = task.checklists.filter(c => c.completed).length;
  const totalChecklists = task.checklists.length;
  const checklistProgress = totalChecklists > 0 ? (completedChecklists / totalChecklists) * 100 : 0;

  return (
    <div className={`grid grid-cols-1 ${isEditMode ? 'lg:grid-cols-3' : ''} gap-6`}>
      {/* Left Column - Checklist Items (2/3 width in edit mode, full width otherwise) */}
      <div className={isEditMode ? 'lg:col-span-2' : ''}>
        <Card className="border border-border/40">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CheckSquare className="h-5 w-5" />
                  Checklist Items
                </CardTitle>
                <CardDescription className="text-xs">
                  Track completion of individual checklist items - {completedChecklists} of {totalChecklists} completed
                </CardDescription>
              </div>
              {totalChecklists > 0 && (
                <div className="text-sm font-medium">{Math.round(checklistProgress)}%</div>
              )}
            </div>
            {totalChecklists > 0 && (
              <Progress value={checklistProgress} className="mt-2" />
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            {task.checklists.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <p>No checklist items yet</p>
                <p className="text-sm mt-1">Add items using the form on the right</p>
              </div>
            ) : (
              task.checklists.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start gap-3 p-3 border rounded-lg hover:bg-accent transition-colors group"
                >
                  <Checkbox
                    checked={item.completed}
                    onCheckedChange={() => handleChecklistToggle(item.id)}
                    className="mt-1"
                    disabled={!isEditMode}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={item.completed ? 'line-through text-muted-foreground' : ''}>
                        {item.title}
                      </span>
                      {item.mandatory && (
                        <Badge variant="destructive" className="text-xs">
                          Required
                        </Badge>
                      )}
                    </div>
                    {item.completed && item.completedBy && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {item.completedBy.includes('Converted') ? (
                          <>
                            ✓ Completed by {item.completedBy} on{' '}
                            {new Date(item.completedAt!).toLocaleDateString()}
                            <span className="ml-2 text-blue-600">→ Converted to subtask</span>
                          </>
                        ) : (
                          <>
                            Completed by {item.completedBy} on{' '}
                            {new Date(item.completedAt!).toLocaleDateString()}
                          </>
                        )}
                      </p>
                    )}
                  </div>
                  {isEditMode && (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {!item.completed && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleConvertToSubtask(item)}
                            title="R4.1: Convert to subtask"
                          >
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Upload evidence"
                          >
                            <Upload className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {!item.completed && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteChecklistItem(item.id)}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Right Column - Add Checklist Item (1/3 width) - Only in Edit Mode */}
      {isEditMode && (
        <div className="lg:col-span-1">
          <Card className="sticky top-4 border border-border/40">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Plus className="h-4 w-4" />
                Add Checklist Item
              </CardTitle>
              <CardDescription className="text-xs">Create a new item to track</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Item Description</label>
                <Textarea
                  placeholder="Enter checklist item description..."
                  value={newChecklistItem}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewChecklistItem(e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleAddChecklistItem();
                    }
                  }}
                  rows={4}
                  className="resize-none"
                />
                <p className="text-xs text-muted-foreground">
                  Press Enter to add, Shift+Enter for new line
                </p>
              </div>
              <Button 
                onClick={handleAddChecklistItem} 
                className="w-full"
                disabled={!newChecklistItem.trim()}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Checklist Item
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
